import { inject, Injectable, DestroyRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router, ActivatedRouteSnapshot, NavigationEnd } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProfileProviderPjControllerService , ProfileProviderPJ } from '../../core/generated';

@Injectable({ providedIn: 'root' })
export class ProviderPJContextService {
  private readonly profileProviderPJService = inject(ProfileProviderPjControllerService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly providerIdSubject = new BehaviorSubject<string | null>(null);
  readonly providerId$ = this.providerIdSubject.asObservable();

  private readonly providerPJSubject = new BehaviorSubject<ProfileProviderPJ | null>(null);
  readonly providerPJ$ = this.providerPJSubject.asObservable();

  private isLoading = false;

  constructor() {
    this.initializeRouteMonitoring();
  }

  private initializeRouteMonitoring(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      debounceTime(50),
      distinctUntilChanged((prev, curr) => {
        const prevId = this.extractProviderIdFromUrl((prev as NavigationEnd).urlAfterRedirects);
        const currId = this.extractProviderIdFromUrl((curr as NavigationEnd).urlAfterRedirects);
        return prevId === currId;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      void this.checkAndUpdateContextFromRoute();
    });

    void this.checkAndUpdateContextFromRoute();
  }

  private async checkAndUpdateContextFromRoute(): Promise<void> {
    if (!this.isProviderPJRoute()) {
      return;
    }

    const currentUrlId = this.findFirstParam(['id', 'providerId', 'parentId']);
    const currentContextId = this.getProviderId();

    if (currentUrlId && currentUrlId !== currentContextId) {
      await this.loadProviderContext(currentUrlId);
    }
  }

  private async loadProviderContext(providerId: string): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      this.setProviderId(providerId);

      const pj = await firstValueFrom(
        this.profileProviderPJService.findById6(Number(providerId))
      );

      if (pj) {
        this.setProviderPJ(pj);
      } else {
        this.providerPJSubject.next(null);
      }
    } catch (error) {
      console.error('ProviderPJContextService: Error loading provider PJ:', error);
      this.clearContext();
    } finally {
      this.isLoading = false;
    }
  }

  private clearContext(): void {
    this.providerIdSubject.next(null);
    this.providerPJSubject.next(null);
  }

  setProviderId(id: string | null): void {
    if (this.providerIdSubject.getValue() !== id) {
      this.providerIdSubject.next(id);
    }
  }

  getProviderId(): string | null {
    return this.providerIdSubject.getValue();
  }

  setProviderPJ(providerPJ: ProfileProviderPJ | null): void {
    const current = this.providerPJSubject.getValue();
    if (current?.id !== providerPJ?.id) {
      this.providerPJSubject.next(providerPJ);
    }
  }

  getProviderPJ(): ProfileProviderPJ | null {
    return this.providerPJSubject.getValue();
  }

  async forceReloadContext(): Promise<void> {
    const parentId = this.findFirstParam(['id', 'providerId', 'parentId']);
    if (!parentId) {
      return;
    }

    this.isLoading = false;
    await this.loadProviderContext(parentId);
  }

  async ensureContextFromUrlIfMissing(): Promise<void> {
    const currentUrlId = this.findFirstParam(['id', 'providerId', 'parentId']);
    const currentContextId = this.getProviderId();
    const providerPJ = this.getProviderPJ();

    if (!currentUrlId) {
      return;
    }

    if (currentUrlId !== currentContextId || !providerPJ) {
      await this.loadProviderContext(currentUrlId);
    }
  }

  private findFirstParam(keys: string[]): string | null {
    const snapshot = this.router.routerState.snapshot.root;
    return this.searchSnapshotForParam(snapshot, keys);
  }

  private searchSnapshotForParam(
    route: ActivatedRouteSnapshot | null,
    keys: string[]
  ): string | null {
    if (!route) return null;

    for (const key of keys) {
      const value = route.paramMap?.get(key);
      if (value) return value;
    }

    for (const child of route.children) {
      const found = this.searchSnapshotForParam(child, keys);
      if (found) return found;
    }

    return null;
  }

  private isProviderPJRoute(): boolean {
    return /^\/providerPJ\/[^\/]+/.test(this.router.url);
  }

  private extractProviderIdFromUrl(url: string): string | null {
    const match = url.match(/\/providerPJ\/([^\/]+)/);
    return match ? match[1] : null;
  }
}
